

import { createContext, useContext, useRef, useState, useEffect, useMemo } from "react";
import { FilterAdvanced } from "../types/Filter";
import { getPref, setPref } from "@/lib/storage/userPrefs";
type Availability = {
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
};

type MetaCalendarContextType = {
  currentDate: string;
  setCurrentDate: React.Dispatch<React.SetStateAction<string>>;
  eventId: string;
  dates: string[];
  timeJump: "15min" | "30min" | "1hour" | "2hour" | "4hour";
  setTimeJump: React.Dispatch<
    React.SetStateAction<"15min" | "30min" | "1hour" | "2hour" | "4hour">
  >;
  timeJumpOptions: string[];
  startTime: string;
  setStartTime: React.Dispatch<React.SetStateAction<string>>;
  archived: boolean;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setArchived: React.Dispatch<React.SetStateAction<boolean>>;
  timeFilter: Availability;
  setTimeFilter: React.Dispatch<React.SetStateAction<Availability>>;
  filter: FilterAdvanced[];
  setFilter: React.Dispatch<React.SetStateAction<FilterAdvanced[]>>;
  appointmentRequestCount: number; // Optional count of slotting requests
  setAppointmentRequestCountState: React.Dispatch<React.SetStateAction<number>>;
  loading: boolean; // Optional loading state
  offeringId: string; // Added offeringId to context
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
  searchValue: string; // Added search value to context
  treatmentIds: string[]; // Added treatment IDs to context
  setTreatmentIds: React.Dispatch<React.SetStateAction<string[]>>; // Setter for
  userAdditionalFields: string[]; // Added user additional fields to context
  setUserAdditionalFields: React.Dispatch<React.SetStateAction<string[]>>; // Setter
  defaultTimeFilter: Availability; // Added default time filter to context
  appointmentExportFields: string[]; // Added appointment export fields to context
  setAppointmentExportFields: React.Dispatch<React.SetStateAction<string[]>>; // Setter for appointment export fields
};

const MetaCalendarContext = createContext<MetaCalendarContextType | undefined>(
  undefined
);

export const MetaCalendarProvider = ({
  children,
  eventId,
  currentDate,
  dates,
  timeJump,
  startTime,
  timeFilterProp,
  filters,
  treatmentIdsProps,
  offeringId,
  appointmentRequestCount,
  userAdditionalFields = [],
  defaultTimeFilter,
  appointmentExportFields: appointmentExportFieldsProp,
  noFetch = false, // Optional prop to skip initial fetch
}: {
  userAdditionalFields: string[];
  appointmentRequestCount: number;
  children: React.ReactNode;
  eventId: string;
  offeringId: string;
  currentDate: string;
  treatmentIdsProps: string[];
  dates: string[];
  timeJump: "15min" | "30min" | "1hour" | "2hour" | "4hour";
  startTime: string;
  timeFilterProp: Availability;
  filters: FilterAdvanced[];
  defaultTimeFilter: Availability;
  appointmentExportFields: string[];
  noFetch?: boolean;
}) => {
  // Initialize with prop values first, then update from cookies
  const [searchValue, setSearchValue] = useState("");
  const [currentDateState, setCurrentDateState] = useState(currentDate);
  const [timeJumpState, setTimeJump] = useState<
    "15min" | "30min" | "1hour" | "2hour" | "4hour"
  >(timeJump);
  const [startTimeState, setStartTime] = useState(startTime);
  const [archived, setArchived] = useState(false);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<FilterAdvanced[]>(filters);
  const [timeFilter, setTimeFilter] = useState(timeFilterProp);
  const [treatmentIds, setTreatmentIds] = useState<string[]>(treatmentIdsProps);
  const [loading, setLoading] = useState(false);
  const [userAdditionalFieldsState, setUserAdditionalFieldsState] =
    useState<string[]>(userAdditionalFields);
  const [appointmentRequestCountState, setAppointmentRequestCountState] =
    useState<number>(appointmentRequestCount);
  const [appointmentExportFields, setAppointmentExportFields] = useState<
    string[]
  >(appointmentExportFieldsProp);

  const timeJumpOptionRef = useRef([
    "15min",
    "30min",
    "1hour",
    "2hour",
    "4hour",
  ]);

  // Load values from cookies on initialization
  useEffect(() => {
    if (noFetch) return; // Skip if noFetch is true

    const savedCurrentDate = getPref("meta-calendar-current-date");
    const savedTimeJump = getPref("meta-calendar-time-jump") as
      | "15min"
      | "30min"
      | "1hour"
      | "2hour"
      | "4hour"
      | null;
    const savedStartTime = getPref("meta-calendar-start-time");

    // Only update if cookie values exist and are valid
    if (savedCurrentDate && dates.includes(savedCurrentDate)) {
      setCurrentDateState(savedCurrentDate);
    }

    if (savedTimeJump && timeJumpOptionRef.current.includes(savedTimeJump)) {
      setTimeJump(savedTimeJump);
    }

    if (savedStartTime) {
      setStartTime(savedStartTime);
    }
  }, [dates, offeringId]); // Added offeringId to dependencies

  // Enhanced setters that also update cookies
  const setCurrentDateWithCookie = (value: React.SetStateAction<string>) => {
    setCurrentDateState((prev) => {
      const newValue = typeof value === "function" ? value(prev) : value;
      setPref("meta-calendar-current-date", newValue);
      return newValue;
    });
  };

  const setTimeJumpWithCookie = (
    value: React.SetStateAction<"15min" | "30min" | "1hour" | "2hour" | "4hour">
  ) => {
    setTimeJump((prev) => {
      const newValue = typeof value === "function" ? value(prev) : value;
      setPref("meta-calendar-time-jump", newValue);
      return newValue;
    });
  };

  const setAppointmentExportCookies = (
    value: React.SetStateAction<string[]>
  ) => {
    setAppointmentExportFields((prev) => {
      const newValue = typeof value === "function" ? value(prev) : value;
      setPref(`${offeringId}-appointmentExport`, JSON.stringify(newValue));
      return newValue;
    });
  };

  const setUserAdditionalFields = (value: React.SetStateAction<string[]>) => {
    setUserAdditionalFieldsState((prev) => {
      const newValue = typeof value === "function" ? value(prev) : value;
      setPref(
        `${offeringId}-additionalGuestFields`,
        JSON.stringify(newValue)
      );
      return newValue;
    });
  };

  const setStartTimeWithCookie = (value: React.SetStateAction<string>) => {
    setStartTime((prev) => {
      const newValue = typeof value === "function" ? value(prev) : value;
      setPref("meta-calendar-start-time", newValue);
      return newValue;
    });
  };

  // Enhanced filter setter that saves to cookies by offeringId
  const setFilterWithCookie = (
    value: React.SetStateAction<FilterAdvanced[]>
  ) => {
    setFilter((prev) => {
      const newValue = typeof value === "function" ? value(prev) : value;
      return newValue;
    });
  };

  // Enhanced time filter setter that saves to cookies by offeringId
  const setTimeFilterWithCookie = (
    value: React.SetStateAction<Availability>
  ) => {
    setTimeFilter((prev) => {
      const newValue = typeof value === "function" ? value(prev) : value;
      return newValue;
    });
  };

  const contextValue = useMemo(() => ({
    userAdditionalFields: userAdditionalFieldsState, setUserAdditionalFields,
    offeringId, startTime: startTimeState, setStartTime: setStartTimeWithCookie,
    timeJumpOptions: timeJumpOptionRef.current, timeJump: timeJumpState,
    setTimeJump: setTimeJumpWithCookie, currentDate: currentDateState,
    setCurrentDate: setCurrentDateWithCookie, appointmentExportFields,
    setAppointmentExportFields: setAppointmentExportCookies, eventId, dates,
    archived, setArchived, page, setPage, timeFilter, setFilter: setFilterWithCookie,
    filter, setTimeFilter: setTimeFilterWithCookie, appointmentRequestCount: appointmentRequestCountState,
    setAppointmentRequestCountState, loading, setSearchValue, searchValue, treatmentIds,
    setTreatmentIds, defaultTimeFilter,
  }), [userAdditionalFieldsState, offeringId, startTimeState, timeJumpState,
    currentDateState, appointmentExportFields, eventId, dates, archived, page,
    timeFilter, filter, appointmentRequestCountState, loading, searchValue, treatmentIds, defaultTimeFilter]);

  return (
    <MetaCalendarContext.Provider value={contextValue}>
      {children}
    </MetaCalendarContext.Provider>
  );
};

export const useMetaCalendar = () => {
  const context = useContext(MetaCalendarContext);
  return context;
};
